import {nest, withProps} from 'recompose'
import {DelayRender} from 'views/Shared/components/DelayRender'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {Button, Modal} from 'antd'
import WorkspaceContext from '../WorkspaceContext';
import useTranslate from 'modules/local/useTranslate'
import {LoginContext} from 'components/LoginContext'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {
    entityRole_create_Api,
    entityRole_delete_Api,
    entityRole_edit_Api,
    entityRole_getById_Api,
    entityRole_getList_Api,
} from 'apis/entityRole'
import {getSystemRoles} from './consts/systemRoles'
import RoleItem from './components/RoleItem'
import CreateRoleModal from './components/CreateRoleModal'
import EditRoleModal from './components/EditRoleModal'
import _ from "lodash"
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";

const OrganizationSettingRoles = () => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const login = useContext(LoginContext)
    const {organization} = useContext(WorkspaceContext)

    const [openCreate, setOpenCreate] = useState(false)
    const [editTarget, setEditTarget] = useState(null)   // { item, schema } | null
    const [refreshKey, setRefreshKey] = useState(0)

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), [])
    const onError = notifyOnError(t)

    // ── Create ────────────────────────────────────────────────────────────────
    const {handleAsyncAction: onCreate, isLoading: creating} = useAsyncAction({
        onError,
        apiInfo: entityRole_create_Api,
        onSuccess: () => {
            successNotify(NotificationActionTypes.CREATE, t)
            setOpenCreate(false)
            refresh()
        },
    })

    const handleCreate = useCallback(
        (values) => {
            onCreate({
                ...values,
                active: !!values?.active ? 1 : 0
            }, {
                type: 'organization',
                id: organization?.id,
            })
        },
        [onCreate, organization]
    )

    // ── Edit: load detail trước khi mở modal ──────────────────────────────────
    const {handleAsyncAction: loadDetail, isLoading: loadingDetail} = useAsyncAction({
        onError,
        apiInfo: entityRole_getById_Api,
        onSuccess: (result, data) => {
            const dateItem = _.get(data, 'response.data', null)
            console.log(111111111, dateItem)
            setEditTarget({
                item: dateItem,
                schema: dateItem?.permissions ?? {},
            })
        },
    })

    const handleOpenEdit = useCallback(
        (item) => {
            loadDetail({}, {role_id: item.id})
        },
        [loadDetail]
    )

    // ── Update ────────────────────────────────────────────────────────────────
    const {handleAsyncAction: onUpdate, isLoading: updating} = useAsyncAction({
        onError,
        apiInfo: entityRole_edit_Api,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            setEditTarget(null)
            refresh()
        },
    })

    const handleUpdate = useCallback(
        // values = { name, active: 0|1, permissions: { dashboard: true, ... } }
        (values) => {
            onUpdate(values, {role_id: editTarget?.item?.id})
        },
        [onUpdate, editTarget]
    )

    // ── Delete ────────────────────────────────────────────────────────────────
    const {handleAsyncAction: onDelete} = useAsyncAction({
        onError,
        apiInfo: entityRole_delete_Api,
        onSuccess: () => {
            successNotify(NotificationActionTypes.DELETE, t)
            refresh()
        },
    })

    const handleDelete = useCallback(
        (item) => {
            Modal.confirm({
                title: t('delete role'),
                content: t('are you sure you want to delete this role'),
                okText: t('delete'),
                okButtonProps: {danger: true},
                cancelText: t('cancel'),
                onOk: () => onDelete({}, {role_id: item.id}),
            })
        },
        [onDelete, t]
    )

    // ── System roles (hard-code, no DB) ───────────────────────────────────────
    const systemRoles = useMemo(() => getSystemRoles(t), [t])

    // ── Pagination custom roles ───────────────────────────────────────────────
    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        isToggleDefault: true,
        renderFilter: NullAsDefault,
        AdvancedFilter: NullAsDefault,
        withKey: createPagerKey([refreshKey]),
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: entityRole_getList_Api,
            query: bindQueryParams([
                {type: 'organization'},
                {id: organization?.id},
            ]),
            renderItem: (item) => (
                <RoleItem
                    key={item.id}
                    item={item}
                    onEdit={handleOpenEdit}
                    onDelete={handleDelete}
                    isLg={isLg}
                />
            ),
            renderWrap: (children) => (
                <div className="flex flex-col divide-y divide-gray-100 list-item-workspace">
                    {children}
                </div>
            ),
        },
    })

    return (
        <div className="flex flex-col h-full">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('roles')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('community_roles_description')}
                    </div>
                </div>

                <Button
                    color="primary" variant="filled"
                    onClick={() => setOpenCreate(true)}
                    className="flex items-center font-semibold rounded-lg no-border">
                    {t('create role')}
                </Button>
            </div>
            <div className="mt-4 flex flex-col gap-2">
                <div className="font-medium text-xs text-slate-400 uppercase tracking-widest">
                    {t('system roles')}
                </div>
                <div className="border border-gray-100 background rounded-xl divide-y divide-gray-100">
                    {systemRoles.map((role) => (
                        <RoleItem key={role.id} item={role} isLg={isLg}/>
                    ))}
                </div>
            </div>

            {/* Custom roles */}
            <div className="mt-4 flex flex-col gap-2">
                <div className="font-medium text-xs text-slate-400 uppercase tracking-widest">
                    {t('custom roles')}
                </div>
                <div className="flex flex-col gap-3">
                    {pager_widget}
                </div>
            </div>

            {/* Create modal */}
            <CreateRoleModal
                open={openCreate}
                loading={creating}
                onCancel={() => setOpenCreate(false)}
                onSubmit={handleCreate}
            />

            {/* Edit modal – chỉ mở khi đã load xong detail */}
            <EditRoleModal
                open={!!editTarget}
                loading={updating || loadingDetail}
                item={editTarget?.item}
                schema={editTarget?.schema}
                onCancel={() => setEditTarget(null)}
                onSubmit={handleUpdate}
            />
        </div>
    )
}

export default nest(
    withProps({lazy: true})(DelayRender),
    OrganizationSettingRoles
)